import { subjectList } from "@examspark/common-meta";
import {
  AssetType,
  BloomsTaxonomy,
  DepthOfKnowledge,
  IssueType,
  Language,
  PropertyType,
  Subject,
} from "@examspark/types";

export const assetTypeMeta = {
  [AssetType.Exam]: {
    label: "Exam",
  },
  [AssetType.Context]: {
    label: "Context/Stimulus",
  },
  [AssetType.Question]: {
    label: "Question",
  },
};

export const issueMeta: {
  [key: string]: {
    label: string;
  };
} = {
  [IssueType.Alignment]: {
    label: "Alignment",
  },
  [IssueType.Appearance]: {
    label: "Appearance",
  },
  [IssueType.Scoring]: {
    label: "Scoring",
  },
  [IssueType.Bias]: {
    label: "Bias",
  },
  [IssueType.Other]: {
    label: "Other",
  },
  [IssueType.Licensing]: {
    label: "Licensing",
  },
  [IssueType.Metadata]: {
    label: "Metadata",
  },
  [IssueType.Preview]: {
    label: "Preview",
  },
  [IssueType.SpellingGrammar]: {
    label: "Spelling & Grammar",
  },
  [IssueType.SubjectMatter]: {
    label: "Subject Matter",
  },
  [IssueType.TranslationReview]: {
    label: "Translation Review",
  },
};

export const propertyTypeMeta = {
  [PropertyType.AssetType]: {
    label: "Asset Type",
  },
  [PropertyType.QuestionType]: { label: "Question Type" },
  [PropertyType.Property]: { label: "Property" },
  [PropertyType.Objective]: { label: "Objective" },
  [PropertyType.DepthOfKnowledge]: { label: "Depth of Knowledge" },
  [PropertyType.Blooms]: { label: "Bloom's Taxonomy" },
  [PropertyType.InteractionCount]: { label: "Interaction Count" },
  [PropertyType.ContextWordCount]: { label: "Context Word Count" },
  [PropertyType.Lexile]: { label: "Lexile" },
  [PropertyType.Language]: { label: "" },
  [PropertyType.Tag]: { label: "Tag" },
  [PropertyType.Keyword]: { label: "Keyword" },
};

export const languageMeta = {
  [Language.English]: {
    label: "English",
  },
  [Language.Spanish]: {
    label: "Spanish",
  },
  [Language.French]: {
    label: "French",
  },
  [Language.German]: {
    label: "German",
  },
  [Language.Vietnamese]: {
    label: "Vietnamese",
  },
};

export const languageList = [
  Language.English,
  Language.Spanish,
  Language.French,
  Language.German,
  Language.Vietnamese,
];

export const coreFourSubjectList = subjectList.filter(
  (subject) =>
    subject !== Subject.CareerTechnicalEducation &&
    subject !== Subject.MultiSubject,
);

/**
 * Returns the appropriate subject list based on workspace ID.
 * Multi-Subject is only available in TCMPC workspace (tx-tcmpc) since it feeds into Bonfire.
 * @param workspaceId - The workspace ID to check
 * @returns Subject list with or without Multi-Subject based on workspace
 */
export const getSubjectListForWorkspace = (workspaceId?: string) => {
  const isTcmpcWorkspace = workspaceId?.includes("tx-tcmpc");
  return isTcmpcWorkspace
    ? subjectList
    : subjectList.filter((subject) => subject !== Subject.MultiSubject);
};

/**
 * Returns the core subject list (excluding Career Technical Education) based on workspace ID.
 * Multi-Subject is only available in TCMPC workspace for classroom components.
 * @param workspaceId - The workspace ID to check
 * @returns Core subject list with or without Multi-Subject based on workspace
 */
export const getCoreSubjectListForWorkspace = (workspaceId?: string) => {
  const isTcmpcWorkspace = workspaceId?.includes("tx-tcmpc");
  return isTcmpcWorkspace
    ? [...coreFourSubjectList, Subject.MultiSubject]
    : coreFourSubjectList;
};

export const dokList = [
  DepthOfKnowledge.DOK1,
  DepthOfKnowledge.DOK2,
  DepthOfKnowledge.DOK3,
  DepthOfKnowledge.DOK4,
];

export const dokMeta = {
  [DepthOfKnowledge.DOK1]: {
    short: "1",
    label: "1: Recall",
  },
  [DepthOfKnowledge.DOK2]: {
    short: "2",
    label: "2: Skill/Concept",
  },
  [DepthOfKnowledge.DOK3]: {
    short: "3",
    label: "3: Strategic Thinking",
  },
  [DepthOfKnowledge.DOK4]: {
    short: "4",
    label: "4: Extended Thinking",
  },
};

export const bloomsList = [
  BloomsTaxonomy.BT1,
  BloomsTaxonomy.BT2,
  BloomsTaxonomy.BT3,
  BloomsTaxonomy.BT4,
  BloomsTaxonomy.BT5,
  BloomsTaxonomy.BT6,
];

export const bloomsMeta = {
  [BloomsTaxonomy.BT1]: {
    short: "1",
    label: "1: Remember",
  },
  [BloomsTaxonomy.BT2]: {
    short: "2",
    label: "2: Understand",
  },
  [BloomsTaxonomy.BT3]: {
    short: "3",
    label: "3: Apply",
  },
  [BloomsTaxonomy.BT4]: {
    short: "4",
    label: "4: Analyze",
  },
  [BloomsTaxonomy.BT5]: {
    short: "5",
    label: "5: Evaluate",
  },
  [BloomsTaxonomy.BT6]: {
    short: "6",
    label: "6: Create",
  },
};
